import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PortalApiError, portalApi } from '../api';
import { getAccessToken, getStoredUser } from '../authStorage';
import { ERROR_CODE_ALREADY_IN_MEETING, ERROR_CODE_MEETING_ENDED, ERROR_CODE_MEETING_NOT_FOUND } from '../constants';
import { withDeviceJoinPrompt } from '../deviceJoin';
import { isPortalAuthJwtRefresh, navigateToMeeting, parsePortalAuthQuery } from '../functions';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
import LoginPage from './LoginPage';
import MeetingInvitePage from './MeetingInvitePage';
/**
 * 按业务错误码选择换票页标题，避免业务失败被笼统显示为「换票失败」。
 */
function resolveExchangeTitle(errorCode) {
    if (errorCode === ERROR_CODE_MEETING_ENDED) {
        return M.MEETING_ENDED_TITLE;
    }
    if (errorCode === ERROR_CODE_MEETING_NOT_FOUND) {
        return M.MEETING_NOT_FOUND_TITLE;
    }
    if (errorCode === ERROR_CODE_ALREADY_IN_MEETING) {
        return M.ALREADY_IN_MEETING_TITLE;
    }
    return M.EXCHANGE_FAILED;
}
/**
 * tokenAuthUrl 换票页：直开会议室无 jwt 时落地此处，登录后展示邀请页，确认后再签发 jwt。
 */
export default function AuthJitsiPage({ onNeedHome }) {
    const [error, setError] = useState('');
    const [errorCode, setErrorCode] = useState();
    const [needLogin, setNeedLogin] = useState(false);
    const [busy, setBusy] = useState(true);
    const [joining, setJoining] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [preview, setPreview] = useState(null);
    const loadingRef = useRef(false);
    const { room } = parsePortalAuthQuery(window.location.search);
    const storedUser = getStoredUser();
    const loginLabel = storedUser?.displayName || storedUser?.phone || M.LOGIN;
    useEffect(() => {
        let cancelled = false;
        async function loadPreview() {
            if (!room) {
                setError(M.MISSING_ROOM);
                setErrorCode(undefined);
                setBusy(false);
                return;
            }
            if (loadingRef.current) {
                return;
            }
            const token = getAccessToken();
            if (!token) {
                logger.info(`邀请页需登录 | room=${room}`);
                setNeedLogin(true);
                setBusy(false);
                return;
            }
            loadingRef.current = true;
            setBusy(true);
            setError('');
            setErrorCode(undefined);
            setPreview(null);
            try {
                const data = await portalApi.preview(token, room);
                if (!cancelled) {
                    setPreview(data);
                    logger.info(`[门户] ${new Date().toISOString()} 会议邀请预览成功`
                        + ` | room=${room} meetingId=${data.id} status=${data.status}`);
                }
            }
            catch (e) {
                if (cancelled) {
                    return;
                }
                const message = e instanceof PortalApiError
                    ? e.message
                    : (e?.message || M.EXCHANGE_FAILED);
                const code = e instanceof PortalApiError ? e.code : undefined;
                if (e instanceof PortalApiError && e.status === 401) {
                    setNeedLogin(true);
                    setError(M.NEED_LOGIN_FIRST);
                    setErrorCode(undefined);
                }
                else {
                    setError(message);
                    setErrorCode(code);
                    logger.error(`邀请预览失败 | room=${room} code=${code || ''} error=${message}`);
                }
            }
            finally {
                loadingRef.current = false;
                if (!cancelled) {
                    setBusy(false);
                }
            }
        }
        loadPreview();
        return () => {
            cancelled = true;
        };
    }, [room, attempt]);
    const onJoin = useCallback(async () => {
        const token = getAccessToken();
        if (!token || !room || joining) {
            return;
        }
        setJoining(true);
        setError('');
        logger.info(`[门户] ${new Date().toISOString()} 邀请页确认加入 | room=${room}`);
        try {
            const refresh = isPortalAuthJwtRefresh(parsePortalAuthQuery(window.location.search).state);
            logger.info(`[门户] ${new Date().toISOString()} 邀请页入会 | room=${room} jwtRefresh=${refresh}`);
            const session = refresh
                ? await portalApi.join(token, room, { reissueToken: true })
                : await withDeviceJoinPrompt(flags => portalApi.join(token, room, flags));
            if (!session) {
                setJoining(false);
                return;
            }
            navigateToMeeting(session);
        }
        catch (e) {
            const message = e instanceof PortalApiError
                ? e.message
                : (e?.message || M.ACTION_FAILED);
            const code = e instanceof PortalApiError ? e.code : undefined;
            if (e instanceof PortalApiError && e.status === 401) {
                setNeedLogin(true);
                setError(M.NEED_LOGIN_FIRST);
                setPreview(null);
            }
            else {
                setError(message);
                setErrorCode(code);
                logger.error(`邀请页加入失败 | room=${room} code=${code || ''} error=${message}`);
            }
            setJoining(false);
        }
    }, [joining, room]);
    if (needLogin) {
        return (React.createElement(LoginPage, { onLoggedIn: () => {
                // 换票页登录成功后必须继续用原 room 加载邀请，不能回首页清掉 room。
                logger.info(`邀请页登录成功，继续预览 | room=${room}`);
                setNeedLogin(false);
                setBusy(true);
                setError('');
                setErrorCode(undefined);
                setAttempt(prev => prev + 1);
            } }));
    }
    if (preview) {
        return (React.createElement(MeetingInvitePage, { busy: joining, error: error, loginLabel: loginLabel, onClose: onNeedHome, onJoin: onJoin, preview: preview }));
    }
    const title = busy ? M.EXCHANGING_TOKEN : resolveExchangeTitle(errorCode);
    return (React.createElement("div", { className: 'portal-login-shell' },
        React.createElement("div", { className: 'portal-login-card portal-auth-card' },
            React.createElement("div", { className: 'portal-brand' },
                React.createElement("div", { className: 'portal-brand-mark' }, "M"),
                React.createElement("h1", null, title),
                room ? React.createElement("p", null,
                    M.MEETING_NO,
                    ": ",
                    room) : null),
            error && error !== title ? React.createElement("div", { className: 'portal-error' }, error) : null,
            !busy && (React.createElement("button", { className: 'portal-primary-btn', onClick: onNeedHome, type: 'button' }, M.CLOSE)))));
}
